<template>
    <BookingFormLayout
        v-bind="
            bookingFormSharedData
                .setContainerClasses('max-w-4xl')
                .getBookingFormLayoutProps()
        "
    >
        <template #content>
            <BorderSeparatedSections
                mainContainerClasses="grid grid-cols-8"
                leftContainerClasses="col-span-12 lg:col-span-3"
                rightContainerClasses="col-span-12 lg:col-span-5"
            >
                <template #left>
                    <MeetingSelectedDetails
                        :company="localCompany"
                        :service="localServiceAggregate.service"
                        :enabledLocationTypes="
                            localServiceAggregate.getUniqueEnabledMeetingLocationTypes()
                        "
                        :meetingLocationIconSize="20"
                        :recurrenceSettings="
                            localServiceAggregate.service.recurrenceSettings
                        "
                        :localizer="localizer"
                    />
                </template>
                <template #right>
                    <div class="space-y-4">
                        <div>
                            <ElementWithIdOnMobile
                                :id="COMPANY_USERS_SECTION_ID"
                            />
                            <BookingFormTitle>
                                {{
                                    localizer.translate(
                                        TextTokenEnum.BookingForm_SelectATeamMember,
                                    )
                                }}
                            </BookingFormTitle>
                        </div>

                        <div class="space-y-2">
                            <template
                                v-for="companyUserAggregate in localCompanyUserAggregates"
                                :key="companyUserAggregate.companyUser.id.value"
                            >
                                <CompanyUserCard
                                    :draftMeetingId="draftMeetingId"
                                    :companyUserAggregate="companyUserAggregate"
                                    :companySlug="localCompany.slug"
                                    :serviceSlug="
                                        localServiceAggregate.service.slug
                                    "
                                    :localizer="localizer"
                                />
                            </template>
                        </div>
                    </div>
                </template>
            </BorderSeparatedSections>
        </template>
    </BookingFormLayout>
</template>

<script setup lang="ts">
    import { onMounted, ref } from 'vue'
    import BookingFormLayout from '@/Shared/Layout/BookingForm/BookingFormLayout.vue'
    import MeetingSelectedDetails from '@/BookingForm/BookingForm/Page/Step/MeetingSelectedDetails.vue'
    import CompanyUserCard from '@/BookingForm/BookingForm/Component/CompanyUserCard.vue'
    import BookingFormSharedData from '@/Shared/Entity/BookingFormSharedData'
    import BookingFormTitle from '@/Shared/Component/BookingFormTitle.vue'
    import BorderSeparatedSections from '@/Shared/Layout/BookingForm/BorderSeparatedSections.vue'
    import usePageScroll from '@/Shared/Hook/usePageScroll'
    import ElementWithIdOnMobile from '@/BookingForm/BookingForm/Component/ElementWithIdOnMobile.vue'
    import LocalizerService from '@/Shared/Service/Localization/LocalizerService'
    import { TextTokenEnum } from '@/Shared/AutoGenerated/LocalizationEnum'
    import ServiceAggregate from '@/App/Service/Entity/ServiceAggregate'
    import CompanyUserAggregate from '@/App/CompanyUser/Entity/CompanyUserAggregate'
    import Company from '@/App/Company/Entity/Company'

    interface ShowBookingFormCompanyUserStepProps {
        bookingFormSharedDataData: BookingFormSharedData
        draftMeetingId: number
        serviceAggregateData: any
        companyUserAggregatesData: any
        companyData: any
    }

    const props = defineProps<ShowBookingFormCompanyUserStepProps>()
    const bookingFormSharedData = BookingFormSharedData.hydrate(
        props.bookingFormSharedDataData,
    )
    const localServiceAggregate = ref(
        ServiceAggregate.hydrateSingle(props.serviceAggregateData),
    )
    const localCompanyUserAggregates = ref(
        CompanyUserAggregate.hydrateMultiple(props.companyUserAggregatesData),
    )
    const localCompany = ref(Company.hydrateSingle(props.companyData))
    const localizer = LocalizerService.make(bookingFormSharedData.currentLocale)

    // -------------------------
    // Lifecycle hooks
    // -------------------------

    onMounted(() => {
        scrollToCompanyUsersSection()
    })

    // -------------------------
    // Scroll to company users section on mobile
    // -------------------------

    const COMPANY_USERS_SECTION_ID = 'COMPANY_USERS_SECTION_ID'

    const scrollToCompanyUsersSection = (): void => {
        usePageScroll().scrollToElement(COMPANY_USERS_SECTION_ID)
    }
</script>
